import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { KeyRound, Plus, Trash2, Copy, Activity, Loader2, ShieldCheck, RefreshCw, Code2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PartnerKeysAdminPanel — mint / list / revoke Game Feed API keys per partner
 * app, with per-key rate limits and usage stats. Super-admin only (backend
 * enforces role; endpoints under /api/game/keys).
 */
const PartnerKeysAdminPanel = ({ language = 'en' }) => {
  const es = language === 'es';
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState('');
  const [rateLimit, setRateLimit] = useState(120);
  const [minting, setMinting] = useState(false);
  const [minted, setMinted] = useState(null); // full key shown once

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/game/keys`, { withCredentials: true });
      setKeys(data.keys || []);
    } catch (e) {
      toast.error(es ? 'Error al cargar claves' : 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const mint = async () => {
    if (label.trim().length < 2) {
      toast.error(es ? 'Ingresa una etiqueta' : 'Enter a label');
      return;
    }
    setMinting(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/game/keys`,
        { label: label.trim(), rate_limit_per_min: Number(rateLimit) || 120 },
        { withCredentials: true },
      );
      setMinted(data.key);
      setLabel('');
      setRateLimit(120);
      await load();
      toast.success(es ? 'Clave creada' : 'Key minted');
    } catch (e) {
      toast.error(es ? 'No se pudo crear la clave' : 'Failed to mint key');
    } finally {
      setMinting(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(es ? `¿Revocar la clave "${k.label}"?` : `Revoke key "${k.label}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/game/keys/${encodeURIComponent(k.id)}`, { withCredentials: true });
      toast.success(es ? 'Clave revocada' : 'Key revoked');
      await load();
    } catch (e) {
      toast.error(es ? 'No se pudo revocar' : 'Revoke failed');
    }
  };

  const rotate = async (k) => {
    if (!window.confirm(es
      ? `¿Rotar la clave "${k.label}"? La clave anterior dejará de funcionar.`
      : `Rotate key "${k.label}"? The old key will stop working.`)) return;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/game/keys/${encodeURIComponent(k.id)}/rotate`, {}, { withCredentials: true });
      setMinted(data.key);
      toast.success(es ? 'Clave rotada — copia la nueva' : 'Key rotated — copy the new one');
      await load();
    } catch (e) {
      toast.error(es ? 'No se pudo rotar' : 'Rotate failed');
    }
  };

  const copy = async (val) => {
    try { await navigator.clipboard.writeText(val); toast.success(es ? 'Copiado' : 'Copied'); } catch (_) {}
  };

  const snippet = (keyVal) => `// Lumera Community Feed — ${keyVal === '<YOUR_API_KEY>' ? 'paste your key' : 'ready to use'}
const LUMERA_URL = "${BACKEND_URL}";
const API_KEY = "${keyVal}";

// Poll the feed
const res = await fetch(\`\${LUMERA_URL}/api/game/feed?limit=20\`, {
  headers: { "X-API-Key": API_KEY },
});
const { items, cursor } = await res.json();
// next poll: fetch(\`\${LUMERA_URL}/api/game/feed?since=\${cursor}\`, ...)

// Or live push via WebSocket:
const ws = new WebSocket(\`\${LUMERA_URL.replace("https","wss")}/api/game/feed/ws?api_key=\${API_KEY}\`);
ws.onmessage = (e) => { const m = JSON.parse(e.data); if (m.items) render(m.items); };`;

  const copySnippet = (keyVal) => copy(snippet(keyVal));

  const totalRequests = keys.reduce((s, k) => s + (k.request_count || 0), 0);

  return (
    <div className="space-y-4" data-testid="partner-keys-panel">
      {/* Usage summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">{es ? 'Claves activas' : 'Active keys'}</p>
          <p className="text-2xl font-bold" data-testid="pk-active-count">{keys.filter((k) => k.active).length}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">{es ? 'Total de solicitudes' : 'Total requests'}</p>
          <p className="text-2xl font-bold flex items-center gap-1"><Activity className="w-4 h-4 text-amber-500" />{totalRequests}</p>
        </CardContent></Card>
        <Card className="hidden sm:block"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">{es ? 'Claves totales' : 'Total keys'}</p>
          <p className="text-2xl font-bold">{keys.length}</p>
        </CardContent></Card>
      </div>

      {/* Mint form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Plus className="w-5 h-5" /> {es ? 'Crear clave de socio' : 'Mint partner key'}</CardTitle>
          <CardDescription>{es ? 'Cada app socia recibe su propia clave con límite y etiqueta.' : 'Each partner app gets its own key with a label and rate limit.'}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label htmlFor="pk-label" className="text-xs">{es ? 'Etiqueta (nombre de la app)' : 'Label (app name)'}</Label>
              <Input id="pk-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Bible Quest" data-testid="pk-label-input" />
            </div>
            <div>
              <Label htmlFor="pk-rate" className="text-xs">{es ? 'Límite / min' : 'Rate limit / min'}</Label>
              <Input id="pk-rate" type="number" min={10} max={6000} value={rateLimit} onChange={(e) => setRateLimit(e.target.value)} data-testid="pk-rate-input" />
            </div>
          </div>
          <Button onClick={mint} disabled={minting} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="pk-mint-btn">
            {minting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            {es ? 'Crear clave' : 'Mint key'}
          </Button>

          {minted && (
            <div className="mt-2 p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20" data-testid="pk-minted-key">
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1 flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> {es ? 'Copia esta clave ahora — solo se muestra una vez' : 'Copy this key now — shown only once'}
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs break-all bg-white dark:bg-slate-800 rounded px-2 py-1">{minted}</code>
                <Button size="sm" variant="outline" onClick={() => copy(minted)} data-testid="pk-copy-minted"><Copy className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => copySnippet(minted)} data-testid="pk-copy-snippet-minted"><Code2 className="w-3.5 h-3.5 mr-1" />{es ? 'Fragmento' : 'Snippet'}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Keys list / usage dashboard */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{es ? 'Claves y uso' : 'Keys & usage'}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" /></div>
          ) : keys.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">{es ? 'Aún no hay claves.' : 'No keys yet.'}</p>
          ) : (
            <div className="space-y-2" data-testid="pk-list">
              {keys.map((k) => (
                <div key={k.id || k.key_masked} className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between p-3 border rounded-lg" data-testid={`pk-item-${k.id || k.key_masked}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{k.label}</span>
                      <Badge variant={k.active ? 'default' : 'secondary'}>{k.active ? (es ? 'Activa' : 'Active') : (es ? 'Revocada' : 'Revoked')}</Badge>
                      <Badge variant="outline">{k.rate_limit_per_min}/min</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 break-all">
                      <code>{k.key_masked}</code> · {k.request_count || 0} {es ? 'solicitudes' : 'reqs'}
                      {k.last_used_at ? ` · ${es ? 'último uso' : 'last'} ${new Date(k.last_used_at).toLocaleString()}` : ` · ${es ? 'sin uso' : 'never used'}`}
                    </p>
                    {k.top_src && k.top_src.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`pk-topsrc-${k.id}`}>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-0.5">{es ? 'referentes' : 'referrers'}:</span>
                        {k.top_src.map((s) => (
                          <Badge key={s.src} variant="secondary" className="text-[10px]">{s.src} · {s.count}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {k.active && (
                      <Button size="sm" variant="ghost" onClick={() => copySnippet('<YOUR_API_KEY>')} title={es ? 'Copiar fragmento de integración' : 'Copy integration snippet'} data-testid={`pk-snippet-${k.id}`}>
                        <Code2 className="w-3.5 h-3.5 mr-1" /> {es ? 'Fragmento' : 'Snippet'}
                      </Button>
                    )}
                    {k.active && k.id && (
                      <Button size="sm" variant="ghost" onClick={() => rotate(k)} data-testid={`pk-rotate-${k.id}`}>
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> {es ? 'Rotar' : 'Rotate'}
                      </Button>
                    )}
                    {k.active && k.id && (
                      <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => revoke(k)} data-testid={`pk-revoke-${k.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> {es ? 'Revocar' : 'Revoke'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerKeysAdminPanel;
